import Backbone from 'backbone';
import cloneDeep from 'lodash/cloneDeep';

export default Backbone.Model.extend({
  firstFetchSuccess: false,
  // gets assigned during parse()
  rawJSON: undefined,
  nestedModels: {},

  constructor(attrs, options = {}) {
    this.listenToOnce(this, 'sync', () => { this.firstFetchSuccess = true; });
    Backbone.Model.call(this, attrs, { ...options, parse: true });

    if (options.initializeNestedModels) {
      this.initializeNestedModels();
    }
  },

  initializeNestedModels() {
    this.set(Object.keys(this.nestedModels).reduce((o, k) => ({
      ...o,
      [k]: this.get(k) || new this.nestedModels[k](undefined, {
        initializeNestedModels: true,
      }),
    }), {}));

    Object.keys(this.nestedModels).filter(k => this.get(k).initializeNestedModels).forEach(k => {
      this.get(k).initializeNestedModels();
    });
  },

  update(attributes, options) {
    let saveAttrs = {};
    if (this.rootKey) {
      saveAttrs[this.rootKey] = attributes;
      if (this.get('mode')) {
        saveAttrs.mode = this.get('mode');
      }
    } else {
      saveAttrs = attributes;

      const mode = this.get('mode');
      if (!saveAttrs.mode && mode) {
        saveAttrs.mode = mode;
      }
    }

    return this.save(null, {
      attrs: saveAttrs,
      wait: true,
      patch: true,
      success: options.success,
      error: options.error,
    });
  },

  parse(attributes = {}) {
    this.rawJSON = cloneDeep(attributes);

    // Comes back as null from the API, so the default never gets set.
    if (!attributes) {
      return {};
    }

    if (this.rootKey && attributes[this.rootKey]) {
      attributes = attributes[this.rootKey];
    }

    return Object.keys(attributes).reduce((o, k) => ({
      ...o,
      [k]: this.mapAttr(k, attributes[k]),
    }), {});
  },

  mapAttr(key, value) {
    if (this.nestedModels[key] &&
      !((value instanceof Backbone.Model) || (value instanceof Backbone.Collection))
    ) {
      if (this.get(key) instanceof Backbone.Model) {
        // if we already have an instance, update to use the new values so we
        // can continue to use bound events from the old instance.
        this.get(key).clear({ silent: true });
        this.get(key).set(this.get(key).parse(value));
        return this.get(key);
      }

      return new this.nestedModels[key](value);
    }

    return value;
  },

  toJSON(useRootKey = true) {
    const out = this.attrsToJSON();
    const { mode } = out;

    if (useRootKey && this.rootKey) {
      return {
        mode,
        [this.rootKey]: out,
      };
    }

    return out;
  },

  attrsToJSON() {
    const attrs = this.attributes;

    return Object.keys(attrs).reduce((o, k) => ({
      ...o,
      [k]: this.nestedModels[k] ? attrs[k].toJSON() : cloneDeep(attrs[k]),
    }), {});
  },

  clone(nullifyIds = true) {
    const previousAttrs = this.attrsToJSON();

    const attrs = {
      ...this.attrsToJSON(),
      id: nullifyIds ? undefined : previousAttrs.id,
    };

    Object.keys(this.attributes).forEach(k => {
      if (attrs[k] && attrs[k].id && nullifyIds) {
        attrs[k].id = undefined;
      }
    });

    return new this.constructor(this.parse(attrs));
  },

  fetchAsPromise(options) {
    return new Promise((res, rej) => {
      this.fetch({
        ...options,
        success: model => res(model),
        error: (_, resp) => rej(resp),
      });
    });
  },
});



// WEBPACK FOOTER //
// ./app/models/base.js